import { Box, BoxProps } from "@mui/material";
import React, { useEffect, useState } from "react";
import { distance, duration, easing, prefersReducedMotion } from "./motion";
import { useInView } from "./useInView";
import { useStaggerDelay } from "./StaggerGroup";

export type RevealVariant = "fadeUp" | "fadeIn" | "fadeLeft" | "fadeRight" | "scaleIn";
export type RevealTrigger = "scroll" | "mount";

export interface RevealProps extends Omit<BoxProps, "children"> {
    children: React.ReactNode;
    variant?: RevealVariant;
    delay?: number;
    duration?: number;
    trigger?: RevealTrigger;
    once?: boolean;
    disabled?: boolean;
    /** Index within a StaggerGroup for automatic delay calculation. */
    staggerIndex?: number;
}

function getHiddenTransform(variant: RevealVariant): string {
    switch (variant) {
        case "fadeUp":
            return `translateY(${distance.md}px)`;
        case "fadeLeft":
            return `translateX(-${distance.md}px)`;
        case "fadeRight":
            return `translateX(${distance.md}px)`;
        case "scaleIn":
            return "scale(0.96)";
        case "fadeIn":
        default:
            return "none";
    }
}

const Reveal: React.FC<RevealProps> = ({
    children,
    variant = "fadeUp",
    delay = 0,
    duration: durationMs = duration.normal,
    trigger = "scroll",
    once = true,
    disabled,
    staggerIndex = 0,
    sx,
    className,
    ...boxProps
}) => {
    const staggerDelay = useStaggerDelay(staggerIndex);
    const totalDelay = delay + staggerDelay;
    const reducedMotion = disabled ?? prefersReducedMotion();
    const { ref, inView } = useInView({ once });
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        if (trigger === "mount") {
            const id = requestAnimationFrame(() => setMounted(true));
            return () => cancelAnimationFrame(id);
        }
    }, [trigger]);

    const visible =
        reducedMotion ||
        (trigger === "mount" ? mounted : inView);

    const transitionDuration = reducedMotion ? 0 : durationMs;
    const transitionDelay = reducedMotion ? 0 : totalDelay * 1000;

    return (
        <Box
            ref={ref}
            className={className ? `reveal ${className}` : "reveal"}
            sx={{
                opacity: visible ? 1 : 0,
                transform: visible ? "none" : getHiddenTransform(variant),
                transition: reducedMotion
                    ? "none"
                    : `opacity ${transitionDuration}ms ${easing.enter} ${transitionDelay}ms, transform ${transitionDuration}ms ${easing.enter} ${transitionDelay}ms`,
                "@media print": {
                    opacity: 1,
                    transform: "none",
                    transition: "none",
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                    breakAfter: "avoid",
                    pageBreakAfter: "avoid",
                },
                ...sx,
            }}
            {...boxProps}
        >
            {children}
        </Box>
    );
};

export default Reveal;
